import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import { QuestionnaireItem, Course, Event, TicketType } from '@/types';
import { Plus, Trash2, Copy } from 'lucide-react';
import { generateId } from '@/lib/utils';
import { normaliseCustomPrefixesForSave } from '@/lib/rank-utils';
import { TagInput } from '@/components/ui/tag-input';
import { EventHeaderCredits } from '@/components/event/EventHeaderCredits';
import { getCreditBookingFeePenceForEvent, MAX_CREDIT_BOOKING_FEE_PENCE, DEFAULT_CREDIT_BOOKING_FEE_PENCE } from '@/lib/platform-booking-fee';
import { TicketMode, TICKET_MODE_DESCRIPTIONS, getEventTicketMode } from '@/lib/ticket-mode';
// Venue selection removed for now; use plain location input

export default function EventSettingsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [unitId, setUnitId] = useState<string>('');

  // Form state
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('19:00');
  const [venueId, setVenueId] = useState('');
  const [location, setLocation] = useState('');
  // Pricing now handled via tickets
  const [maxCapacity, setMaxCapacity] = useState('');
  const [rsvpDeadline, setRsvpDeadline] = useState('');
  const [paymentDeadline, setPaymentDeadline] = useState('');
  const [dresscode, setDresscode] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [eventStatus, setEventStatus] = useState<'draft' | 'interest-only' | 'published' | 'closed' | 'completed' | 'deleted'>('draft');
  const [isPublic, setIsPublic] = useState(false);
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireItem[]>([]);
  // Ticket configuration with per-ticket courses
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [ticketMode, setTicketModeState] = useState<TicketMode>('mealChoice');
  const [unitEntityName, setUnitEntityName] = useState('Unit');
  const [requirePrefixRankUnitInfo, setRequirePrefixRankUnitInfo] = useState(true);
  const [allowFutureEventsInterest, setAllowFutureEventsInterest] = useState(true);
  const [useCustomPrefixes, setUseCustomPrefixes] = useState(false);
  const [customPrefixes, setCustomPrefixes] = useState<string[]>([]);
  const [guestCoversCreditCost, setGuestCoversCreditCost] = useState(true);
  const [creditBookingFeePoundsInput, setCreditBookingFeePoundsInput] = useState('0.60');
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [bannerUrl, setBannerUrl] = useState<string>('');
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>('');
  const [backgroundImagePreview, setBackgroundImagePreview] = useState<string | null>(null);
  const [uploadingBackgroundImage, setUploadingBackgroundImage] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string>('');

  const unitInfoLabel = unitEntityName || 'Unit';

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;

      try {
        // Query all units to find the event
        const unitsSnapshot = await getDocs(collection(db, 'units'));

        for (const unitDoc of unitsSnapshot.docs) {
          const eventDoc = await getDoc(
            doc(db, 'units', unitDoc.id, 'events', eventId)
          );

          if (eventDoc.exists()) {
            const data = eventDoc.data();
            const eventDate = data.date?.toDate() || new Date();
            const unitData = unitDoc.data();
            let derivedEntityName = (unitData as any)?.entityName || 'Unit';

            if ((unitData as any)?.typeId) {
              try {
                const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
                if (settingsDoc.exists()) {
                  const platformData = settingsDoc.data();
                  const unitTypes = (platformData?.unitTypes || []) as Array<{ id: string; entityName?: string }>;
                  const matchedType = unitTypes.find((type) => type.id === (unitData as any).typeId);
                  if (matchedType?.entityName) {
                    derivedEntityName = matchedType.entityName;
                  }
                }
              } catch (settingsError) {
                console.error('Error fetching unit type metadata:', settingsError);
              }
            }

            const eventData: Event = {
              id: eventDoc.id,
              unitId: unitDoc.id,
              name: data.name,
              date: eventDate,
              location: data.location,
              venueId: data.venueId,
              questionnaire: data.questionnaire || [],
              courses: data.courses || [],
              tickets: data.tickets || [],
              ticketMode: data.ticketMode === 'multiTenant' ? 'multiTenant' : 'mealChoice',
              currentAttendees: data.currentAttendees || 0,
              maxCapacity: data.maxCapacity,
              waitlistEnabled: data.waitlistEnabled || false,
              waitlist: data.waitlist || [],
              rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
              paymentDeadline: data.paymentDeadline?.toDate() || (data.rsvpDeadline?.toDate() || eventDate),
              eventStatus: data.eventStatus || 'draft',
              dresscode: data.dresscode,
              customInstructions: data.customInstructions,
              isPublic: data.isPublic ?? false,
              requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
              allowFutureEventsInterest: data.allowFutureEventsInterest ?? true,
              useCustomPrefixes: Boolean(data.useCustomPrefixes),
              customPrefixes: Array.isArray(data.customPrefixes)
                ? data.customPrefixes.filter((p: unknown) => typeof p === 'string' && p.trim() !== '')
                : [],
              guestCoversCreditCost: data.guestCoversCreditCost ?? false,
              creditBookingFeePence:
                typeof data.creditBookingFeePence === 'number' ? data.creditBookingFeePence : undefined,
              logoUrl: data.logoUrl,
              bannerUrl: data.bannerUrl,
              backgroundImageUrl: data.backgroundImageUrl,
              backgroundColor: data.backgroundColor,
              createdBy: data.createdBy,
              createdAt: data.createdAt?.toDate() || new Date(),
            };

            setUnitEntityName(derivedEntityName);
            setEvent(eventData);
            setUnitId(unitDoc.id);

            // Populate form fields
            setEventName(eventData.name);
            setEventDate(eventData.date.toISOString().split('T')[0]);
            setEventTime(eventData.date.toTimeString().slice(0, 5));
            setVenueId(eventData.venueId || (eventData.location ? '__custom__' : ''));
            setLocation(eventData.location);
            setMaxCapacity(eventData.maxCapacity?.toString() || '');
            setRsvpDeadline(eventData.rsvpDeadline.toISOString().split('T')[0]);
            setPaymentDeadline(eventData.paymentDeadline ? eventData.paymentDeadline.toISOString().split('T')[0] : '');
            setDresscode(eventData.dresscode || '');
            setCustomInstructions(eventData.customInstructions || '');
            setEventStatus(eventData.eventStatus as 'draft' | 'interest-only' | 'published' | 'closed' | 'completed' | 'deleted');
            setIsPublic(Boolean(eventData.isPublic));
            setRequirePrefixRankUnitInfo(eventData.requirePrefixRankUnitInfo ?? true);
            setAllowFutureEventsInterest(eventData.allowFutureEventsInterest ?? true);
            setUseCustomPrefixes(Boolean(eventData.useCustomPrefixes));
            setCustomPrefixes(eventData.customPrefixes || []);
            setGuestCoversCreditCost(eventData.guestCoversCreditCost ?? false);
            setCreditBookingFeePoundsInput(
              (getCreditBookingFeePenceForEvent(eventData) / 100).toFixed(2),
            );
            setQuestionnaire(eventData.questionnaire || []);
            setTickets(eventData.tickets || []);
            setTicketModeState(getEventTicketMode(eventData));
            setLogoUrl(eventData.logoUrl || '');
            setLogoPreview(eventData.logoUrl || null);
            setBannerUrl(eventData.bannerUrl || '');
            setBannerPreview(eventData.bannerUrl || null);
            setBackgroundImageUrl(eventData.backgroundImageUrl || '');
            setBackgroundImagePreview(eventData.backgroundImageUrl || null);
            setBackgroundColor(eventData.backgroundColor || '');
            break;
          }
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        showError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  const addQuestion = () => {
    setQuestionnaire([
      ...questionnaire,
      {
        id: generateId(),
        question: '',
        type: 'text',
        required: false,
      },
    ]);
  };

  const removeQuestion = (id: string) => {
    setQuestionnaire(questionnaire.filter((q) => q.id !== id));
  };

  const updateQuestion = (id: string, field: keyof QuestionnaireItem, value: any) => {
    setQuestionnaire(
      questionnaire.map((q) => (q.id === id ? { ...q, [field]: value } : q))
    );
  };

  // Ticket configuration handlers
  const addTicket = () => {
    setTickets([
      ...tickets,
      {
        id: generateId(),
        name: '',
        price: 0,
        totalAvailable: undefined,
        restricted: false,
        courses: [],
      },
    ]);
  };

  const removeTicket = (id: string) => {
    setTickets(tickets.filter((t) => t.id !== id));
  };

  const duplicateTicket = (id: string) => {
    const ticketToDuplicate = tickets.find((t) => t.id === id);
    if (!ticketToDuplicate) return;

    // Create a deep copy of the ticket with new IDs
    const duplicatedTicket: TicketType = {
      ...ticketToDuplicate,
      id: generateId(),
      name: `${ticketToDuplicate.name} (Copy)`,
      courses: (ticketToDuplicate.courses || []).map((course) => ({
        ...course,
        id: generateId(),
      })),
    };

    // Find the index of the original ticket and insert the duplicate right after it
    const originalIndex = tickets.findIndex((t) => t.id === id);
    const newTickets = [...tickets];
    newTickets.splice(originalIndex + 1, 0, duplicatedTicket);
    setTickets(newTickets);
  };

  const handleTicketModeChange = async (mode: TicketMode) => {
    if (mode === ticketMode) return;
    if ((event?.currentAttendees || 0) > 0) {
      const result = await confirmAction({
        title: 'Change ticket mode?',
        text: 'This event already has RSVPs. Switching ticket modes will not change or migrate existing RSVP records — only new RSVPs will use the new mode.',
        confirmButtonText: 'Yes, change it',
        icon: 'warning',
      });
      if (!result.isConfirmed) return;
    }
    setTicketModeState(mode);
  };

  const updateTicketField = (id: string, field: keyof TicketType, value: any) => {
    setTickets(tickets.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const addCourseToTicket = (ticketId: string) => {
    setTickets(
      tickets.map((t) =>
        t.id === ticketId
          ? {
              ...t,
              courses: [
                ...(t.courses || []),
                { id: generateId(), name: '', options: [] },
              ],
            }
          : t
      )
    );
  };

  const removeCourseFromTicket = (ticketId: string, courseId: string) => {
    setTickets(
      tickets.map((t) =>
        t.id === ticketId
          ? { ...t, courses: (t.courses || []).filter((c) => c.id !== courseId) }
          : t
      )
    );
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showError('Image size must be less than 2MB');
      return;
    }

    if (!unitId || !eventId) {
      showError('Event information is missing');
      return;
    }

    setUploadingLogo(true);
    try {
      // Delete old logo if exists
      if (logoUrl) {
        try {
          const oldLogoRef = ref(storage, `units/${unitId}/events/${eventId}/logo/logo`);
          await deleteObject(oldLogoRef);
        } catch (error) {
          // Ignore if file doesn't exist
        }
      }

      // Upload new logo
      const logoRef = ref(storage, `units/${unitId}/events/${eventId}/logo/logo`);
      await uploadBytes(logoRef, file);
      const newLogoUrl = await getDownloadURL(logoRef);

      // Update Firestore
      await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
        logoUrl: newLogoUrl,
      });

      setLogoUrl(newLogoUrl);
      setLogoPreview(newLogoUrl);
      showSuccess('Event logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      showError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!unitId || !eventId) {
      showError('Event information is missing');
      return;
    }

    if (!logoUrl) return;

    try {
      // Delete logo from storage
      const logoRef = ref(storage, `units/${unitId}/events/${eventId}/logo/logo`);
      await deleteObject(logoRef);

      // Update Firestore
      await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
        logoUrl: null,
      });

      setLogoUrl('');
      setLogoPreview(null);
      showSuccess('Event logo removed successfully!');
    } catch (error) {
      console.error('Error removing logo:', error);
      showError('Failed to remove logo');
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB for banner)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    if (!unitId || !eventId) {
      showError('Event information is missing');
      return;
    }

    setUploadingBanner(true);
    try {
      // Delete old banner if exists
      if (bannerUrl) {
        try {
          const oldBannerRef = ref(storage, `units/${unitId}/events/${eventId}/banner/banner`);
          await deleteObject(oldBannerRef);
        } catch (error) {
          // Ignore if file doesn't exist
        }
      }

      // Upload new banner
      const bannerRef = ref(storage, `units/${unitId}/events/${eventId}/banner/banner`);
      await uploadBytes(bannerRef, file);
      const newBannerUrl = await getDownloadURL(bannerRef);

      // Update Firestore
      await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
        bannerUrl: newBannerUrl,
      });

      setBannerUrl(newBannerUrl);
      setBannerPreview(newBannerUrl);
      showSuccess('Banner uploaded successfully!');
    } catch (error) {
      console.error('Error uploading banner:', error);
      showError('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleRemoveBanner = async () => {
    if (!unitId || !eventId) {
      showError('Event information is missing');
      return;
    }

    if (!bannerUrl) return;

    try {
      // Delete banner from storage
      const bannerRef = ref(storage, `units/${unitId}/events/${eventId}/banner/banner`);
      await deleteObject(bannerRef);

      // Update Firestore
      await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
        bannerUrl: null,
      });

      setBannerUrl('');
      setBannerPreview(null);
      showSuccess('Banner removed successfully!');
    } catch (error) {
      console.error('Error removing banner:', error);
      showError('Failed to remove banner');
    }
  };

  const handleBackgroundImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB for background)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    if (!unitId || !eventId) {
      showError('Event information is missing');
      return;
    }

    setUploadingBackgroundImage(true);
    try {
      // Delete old background image if exists
      if (backgroundImageUrl) {
        try {
          const oldBgRef = ref(storage, `units/${unitId}/events/${eventId}/background/background`);
          await deleteObject(oldBgRef);
        } catch (error) {
          // Ignore if file doesn't exist
        }
      }

      // Upload new background image
      const bgRef = ref(storage, `units/${unitId}/events/${eventId}/background/background`);
      await uploadBytes(bgRef, file);
      const newBgUrl = await getDownloadURL(bgRef);

      // Update Firestore
      await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
        backgroundImageUrl: newBgUrl,
        backgroundColor: null, // Clear color if image is set
      });

      setBackgroundImageUrl(newBgUrl);
      setBackgroundImagePreview(newBgUrl);
      setBackgroundColor(''); // Clear color state when image is set
      showSuccess('Background image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading background image:', error);
      showError('Failed to upload background image');
    } finally {
      setUploadingBackgroundImage(false);
    }
  };

  const handleRemoveBackgroundImage = async () => {
    if (!unitId || !eventId) {
      showError('Event information is missing');
      return;
    }

    if (!backgroundImageUrl) return;

    try {
      // Delete background image from storage
      const bgRef = ref(storage, `units/${unitId}/events/${eventId}/background/background`);
      await deleteObject(bgRef);

      // Update Firestore
      await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
        backgroundImageUrl: null,
      });

      setBackgroundImageUrl('');
      setBackgroundImagePreview(null);
      showSuccess('Background image removed successfully!');
    } catch (error) {
      console.error('Error removing background image:', error);
      showError('Failed to remove background image');
    }
  };

  const updateTicketCourse = (ticketId: string, courseId: string, field: keyof Course, value: any) => {
    setTickets(
      tickets.map((t) =>
        t.id === ticketId
          ? {
              ...t,
              courses: (t.courses || []).map((c) => (c.id === courseId ? { ...c, [field]: value } : c)),
            }
          : t
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!unitId || !eventId) {
      showError('Event information is missing');
      return;
    }

    if (!currentUser) {
      showError('You must be logged in to update an event');
      return;
    }

    // Combine date and time
    const eventDateTime = new Date(`${eventDate}T${eventTime}`);

    setSaving(true);
    try {
      let creditBookingFeePence: number | null = null;
      if (guestCoversCreditCost) {
        const normalized = creditBookingFeePoundsInput.trim().replace(/,/g, '');
        const pounds = Number.parseFloat(normalized);
        if (!Number.isFinite(pounds) || pounds < 0 || Math.round(pounds * 100) > MAX_CREDIT_BOOKING_FEE_PENCE) {
          showError('Enter a booking fee between £0.00 and £5.00.');
          setSaving(false);
          return;
        }
        creditBookingFeePence = Math.round(pounds * 100);
      }

      const defaultRsvpDeadline = new Date(eventDateTime.getTime() - 7 * 24 * 60 * 60 * 1000);
      const rsvpDeadlineDate = rsvpDeadline ? new Date(rsvpDeadline) : defaultRsvpDeadline;
      // Payment deadline defaults to RSVP deadline if not set
      const paymentDeadlineDate = paymentDeadline ? new Date(paymentDeadline) : rsvpDeadlineDate;
      const eventData = {
        name: eventName,
        date: eventDateTime,
        location,
        venueId: venueId && venueId !== '__custom__' ? venueId : null,
        questionnaire,
        ticketMode,
        tickets: tickets
          .map((t) => ({
            ...t,
            name: (t.name || '').trim(),
            price: Number(t.price) || 0,
            totalAvailable: t.totalAvailable ? Number(t.totalAvailable) : null,
            restricted: Boolean(t.restricted),
            courses: ticketMode === 'multiTenant'
              ? []
              : (t.courses || []).filter((c) => c.name && c.options && c.options.length > 0),
          }))
          .filter((t) => t.name),
        maxCapacity: maxCapacity ? parseInt(maxCapacity) : null,
        waitlistEnabled: !!maxCapacity,
        rsvpDeadline: rsvpDeadlineDate,
        paymentDeadline: paymentDeadlineDate,
        eventStatus,
        dresscode,
        customInstructions,
        isPublic,
        requirePrefixRankUnitInfo,
        allowFutureEventsInterest,
        useCustomPrefixes,
        customPrefixes: normaliseCustomPrefixesForSave(customPrefixes),
        guestCoversCreditCost,
        creditBookingFeePence: guestCoversCreditCost ? creditBookingFeePence : null,
        ...(guestCoversCreditCost
          ? {}
          : {
              creditCostRecovery: {
                enabled: false,
                amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE,
              },
            }),
        logoUrl: logoUrl || null,
        bannerUrl: bannerUrl || null,
        backgroundImageUrl: backgroundImageUrl || null,
        backgroundColor: backgroundColor || null,
        updatedAt: new Date(),
      };

      await updateDoc(doc(db, 'units', unitId, 'events', eventId), eventData);

      showSuccess('Event settings updated successfully.');

      navigate(`/event/${eventId}`);
    } catch (error) {
      console.error('Error updating event:', error);
      showError('Failed to update event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading event settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Event Not Found</CardTitle>
              <CardDescription>The event you're trying to edit doesn't exist.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 w-full min-w-0 py-6 sm:py-8">
        <DashboardContentContainer>
          <div className="mb-6 sm:mb-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" onClick={() => navigate(`/event/${eventId}`)}>
              ← Back to Event Dashboard
            </Button>
            <EventHeaderCredits unitId={unitId} />
          </div>

          <h1 className="mb-6 text-2xl font-bold sm:mb-8 sm:text-3xl">Event Settings</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Essential details about your event</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-1.5">
                  <Label htmlFor="eventName">Event Name *</Label>
                  <Input
                    id="eventName"
                    type="text"
                    placeholder="e.g., Installation, Festive Board, Ladies Night"
                    autoComplete="off"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">This will appear on invites and the RSVP page.</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="eventDate">Event Date *</Label>
                    <Input
                      id="eventDate"
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="eventTime">Event Time *</Label>
                    <Input
                      id="eventTime"
                      type="time"
                      value={eventTime}
                      onChange={(e) => setEventTime(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="customLocation">Location *</Label>
                  <Input
                    id="customLocation"
                    type="text"
                    placeholder="e.g., Cardiff Masonic Hall, Guildford Street, Cardiff"
                    autoComplete="off"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">Use a clear venue name and full address.</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="rsvpDeadline">RSVP Deadline</Label>
                    <Input
                      id="rsvpDeadline"
                      type="date"
                      value={rsvpDeadline}
                      onChange={(e) => setRsvpDeadline(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Defaults to 7 days before event
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="paymentDeadline">Payment Deadline</Label>
                    <Input
                      id="paymentDeadline"
                      type="date"
                      value={paymentDeadline}
                      onChange={(e) => setPaymentDeadline(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Defaults to RSVP deadline if left blank
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="maxCapacity">Maximum Capacity (Optional)</Label>
                    <Input
                      id="maxCapacity"
                      type="number"
                      min="1"
                      placeholder="120"
                      value={maxCapacity}
                      onChange={(e) => setMaxCapacity(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="dresscode">Dress Code</Label>
                    <Input
                      id="dresscode"
                      type="text"
                      placeholder="e.g., Black Tie, Lounge Suit"
                      autoComplete="off"
                      value={dresscode}
                      onChange={(e) => setDresscode(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Optional. Shown on the RSVP page.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="eventStatus">Event Status *</Label>
                    <select
                      id="eventStatus"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={eventStatus}
                      onChange={(e) => setEventStatus(e.target.value as 'draft' | 'interest-only' | 'published' | 'closed' | 'completed')}
                    >
                      <option value="draft">Draft</option>
                      <option value="interest-only">Interest Only</option>
                      <option value="published">Published</option>
                      <option value="closed">Closed</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="isPublic"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    label="Make this event public"
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, this event will be displayed on the Upcoming Events page on the dining homepage, making it visible to all visitors
                  </p>
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="requirePrefixRankUnitInfo"
                    checked={requirePrefixRankUnitInfo}
                    onChange={(e) => setRequirePrefixRankUnitInfo(e.target.checked)}
                    label={`Require Prefixes, Rank and ${unitInfoLabel} Information`}
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, attendees must provide their prefix, rank, and {unitInfoLabel} details during RSVP.
                  </p>
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="allowFutureEventsInterest"
                    checked={allowFutureEventsInterest}
                    onChange={(e) => setAllowFutureEventsInterest(e.target.checked)}
                    label="Allow attendees to register interest in future events"
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, the RSVP form offers an &quot;Invite me to future events&quot; option that adds the attendee to this unit&apos;s guest list.
                  </p>
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="useCustomPrefixes"
                    checked={useCustomPrefixes}
                    onChange={(e) => setUseCustomPrefixes(e.target.checked)}
                    label="Use custom prefixes"
                  />
                  {useCustomPrefixes && (
                    <div className="ml-6 space-y-1">
                      <TagInput
                        tags={customPrefixes}
                        onChange={setCustomPrefixes}
                        placeholder="Type prefix and press Enter"
                      />
                      <p className="text-xs text-muted-foreground">
                        Press Enter after each prefix. These replace the unit&apos;s default prefix list for this event.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Checkbox
                    id="guestCoversCreditCost"
                    checked={guestCoversCreditCost}
                    onChange={(e) => setGuestCoversCreditCost(e.target.checked)}
                    label="Charge a Booking Fee to the Guest to cover the Credit cost"
                  />
                  <p className="text-xs text-muted-foreground ml-6">
                    When enabled, a booking fee will be added per guest. 1 Credit is consumed per guest.
                  </p>
                  {guestCoversCreditCost && (
                    <div className="ml-6 mt-3 max-w-xs space-y-2">
                      <Label htmlFor="creditBookingFeePounds">Booking fee (£)</Label>
                      <p className="text-xs text-muted-foreground">
                        Per paying RSVP slot, in addition to ticket price. Enter £0.00 to £5.00 (for example 0.60 for
                        60p).
                      </p>
                      <Input
                        id="creditBookingFeePounds"
                        type="number"
                        min={0}
                        max={MAX_CREDIT_BOOKING_FEE_PENCE / 100}
                        step={0.01}
                        value={creditBookingFeePoundsInput}
                        onChange={(e) => setCreditBookingFeePoundsInput(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Branding */}
            <Card>
              <CardHeader>
                <CardTitle>Branding</CardTitle>
                <CardDescription>
                  Customize the visual appearance of your event on the RSVP page
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo */}
                <div className="space-y-2">
                  <Label htmlFor="eventLogoUpload">Logo</Label>
                  {logoPreview && (
                    <div className="mb-2 p-2 border rounded bg-gray-50">
                      <img
                        src={logoPreview}
                        alt="Event Logo"
                        className="h-16 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      id="eventLogoUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="flex-1"
                    />
                    {logoPreview && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveLogo}
                        disabled={uploadingLogo}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {uploadingLogo && (
                    <p className="text-xs text-muted-foreground">Uploading...</p>
                  )}
                </div>

                {/* Banner */}
                <div className="space-y-2">
                  <Label htmlFor="eventBannerUpload">Banner</Label>
                  {bannerPreview && (
                    <div className="mb-2 p-2 border rounded bg-gray-50">
                      <img
                        src={bannerPreview}
                        alt="Event Banner"
                        className="w-full max-h-24 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      id="eventBannerUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleBannerUpload}
                      disabled={uploadingBanner}
                      className="flex-1"
                    />
                    {bannerPreview && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveBanner}
                        disabled={uploadingBanner}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {uploadingBanner && (
                    <p className="text-xs text-muted-foreground">Uploading...</p>
                  )}
                </div>

                {/* Background */}
                <div className="space-y-2">
                  <Label htmlFor="eventBackgroundImageUpload">Background</Label>
                  {backgroundImagePreview && (
                    <div className="mb-2 p-2 border rounded bg-gray-50">
                      <img
                        src={backgroundImagePreview}
                        alt="Event Background"
                        className="w-full max-h-24 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      id="eventBackgroundImageUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleBackgroundImageUpload}
                      disabled={uploadingBackgroundImage}
                      className="flex-1"
                    />
                    {backgroundImagePreview && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveBackgroundImage}
                        disabled={uploadingBackgroundImage}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {uploadingBackgroundImage && (
                    <p className="text-xs text-muted-foreground">Uploading...</p>
                  )}
                  <div className="flex gap-2 items-center">
                    <Label htmlFor="backgroundColor" className="text-sm whitespace-nowrap">or Color:</Label>
                    <Input
                      id="backgroundColor"
                      type="color"
                      value={backgroundColor || '#ffffff'}
                      onChange={async (e) => {
                        const newColor = e.target.value;
                        setBackgroundColor(newColor);
                        // Clear background image when color is set
                        if (backgroundImageUrl && newColor && unitId && eventId) {
                          try {
                            // Delete background image from storage
                            const bgRef = ref(storage, `units/${unitId}/events/${eventId}/background/background`);
                            await deleteObject(bgRef);
                            // Update Firestore
                            await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
                              backgroundImageUrl: null,
                            });
                            setBackgroundImagePreview(null);
                            setBackgroundImageUrl('');
                          } catch (error) {
                            console.error('Error clearing background image:', error);
                          }
                        }
                      }}
                      className="w-16 h-8"
                    />
                    <Input
                      type="text"
                      placeholder="#ffffff"
                      value={backgroundColor}
                      onChange={async (e) => {
                        const newColor = e.target.value;
                        setBackgroundColor(newColor);
                        // Clear background image when color is set
                        if (backgroundImageUrl && newColor && unitId && eventId) {
                          try {
                            // Delete background image from storage
                            const bgRef = ref(storage, `units/${unitId}/events/${eventId}/background/background`);
                            await deleteObject(bgRef);
                            // Update Firestore
                            await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
                              backgroundImageUrl: null,
                            });
                            setBackgroundImagePreview(null);
                            setBackgroundImageUrl('');
                          } catch (error) {
                            console.error('Error clearing background image:', error);
                          }
                        }
                      }}
                      className="flex-1"
                    />
                    {backgroundColor && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBackgroundColor('');
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ticket Configuration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Ticket Configuration</CardTitle>
                    <CardDescription>
                      Define ticket types, pricing, availability, and per-ticket course options
                    </CardDescription>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addTicket}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Ticket Type
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  {(Object.keys(TICKET_MODE_DESCRIPTIONS) as TicketMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => handleTicketModeChange(mode)}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        ticketMode === mode ? 'border-primary bg-primary/5' : 'border-input'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-3 w-3 shrink-0 rounded-full border ${
                            ticketMode === mode ? 'border-primary bg-primary' : 'border-input'
                          }`}
                        />
                        <span className="text-sm font-medium">{TICKET_MODE_DESCRIPTIONS[mode].label}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {TICKET_MODE_DESCRIPTIONS[mode].description}
                      </p>
                    </button>
                  ))}
                </div>

                {tickets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tickets configured. Click "Add Ticket Type" to create one.</p>
                ) : (
                  tickets.map((ticket, tIndex) => (
                    <div key={ticket.id} className="rounded-lg border p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <Label>Ticket {tIndex + 1}</Label>
                        <div className="flex gap-2">
                          <Button type="button" variant="ghost" size="icon" onClick={() => duplicateTicket(ticket.id)} title="Duplicate ticket">
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeTicket(ticket.id)} title="Delete ticket">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <div className="space-y-2">
                          <Label>Ticket Name</Label>
                          <Input
                            placeholder="Standard / Adult / Child"
                            value={ticket.name}
                            onChange={(e) => updateTicketField(ticket.id, 'name', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Price (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="35.00"
                            value={ticket.price}
                            onChange={(e) => updateTicketField(ticket.id, 'price', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity Available (optional)</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="e.g. 100"
                            value={ticket.totalAvailable ?? ''}
                            onChange={(e) => updateTicketField(ticket.id, 'totalAvailable', e.target.value ? parseInt(e.target.value) : undefined)}
                          />
                        </div>
                        <div className="space-y-2 flex flex-col justify-end">
                          <Label id={`restricted-${ticket.id}`}>Access</Label>
                          <Checkbox
                            id={`restricted-${ticket.id}`}
                            checked={Boolean(ticket.restricted)}
                            onChange={(e) => updateTicketField(ticket.id, 'restricted', e.target.checked)}
                            label="Restricted ticket"
                          />
                          <p className="text-xs text-muted-foreground">
                            Invited guests may only share restricted tickets they hold or were assigned
                          </p>
                        </div>
                      </div>
                      {ticketMode === 'mealChoice' && (
                        <div className="pt-2">
                          <div className="flex items-center justify-between mb-3">
                            <Label>Per-ticket Course Configuration</Label>
                            <Button type="button" variant="outline" size="sm" onClick={() => addCourseToTicket(ticket.id)}>
                              <Plus className="mr-2 h-4 w-4" />
                              Add Course
                            </Button>
                          </div>
                          {(ticket.courses && ticket.courses.length > 0) ? (
                            (ticket.courses || []).map((course) => (
                              <div key={course.id} className="rounded-md border p-3 mb-3">
                                <div className="flex items-center justify-between mb-3">
                                  <Label>Course</Label>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => removeCourseFromTicket(ticket.id, course.id)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  <Input
                                    placeholder="Course name (e.g., Starter, Main, Dessert)"
                                    value={course.name}
                                    onChange={(e) => updateTicketCourse(ticket.id, course.id, 'name', e.target.value)}
                                  />
                                  <div className="space-y-1">
                                    <TagInput
                                      tags={course.options}
                                      onChange={(newOptions) => updateTicketCourse(ticket.id, course.id, 'options', newOptions)}
                                      placeholder="Type option and press comma or enter (e.g., Beef Wellington)"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      Press comma or enter after each option to add it as a dining option
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-muted-foreground">No courses for this ticket type. Add courses if meal selections are required.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Questionnaire */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Additional Questions</CardTitle>
                    <CardDescription>
                      Add any additional questions for your guests (dietary requirements, etc.)
                    </CardDescription>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Question
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {questionnaire.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No additional questions. Click "Add Question" to add one.</p>
                ) : (
                  questionnaire.map((q, index) => (
                    <div key={q.id} className="rounded-lg border p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <Label>Question {index + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeQuestion(q.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        <Input
                          placeholder="Question text"
                          value={q.question}
                          onChange={(e) => updateQuestion(q.id, 'question', e.target.value)}
                        />
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={q.type}
                          onChange={(e) => updateQuestion(q.id, 'type', e.target.value)}
                        >
                          <option value="text">Text Input</option>
                          <option value="textarea">Long Text</option>
                          <option value="select">Single Choice</option>
                          <option value="multiselect">Multiple Choice</option>
                        </select>
                        {(q.type === 'select' || q.type === 'multiselect') && (
                          <div className="space-y-1">
                            <TagInput
                              tags={q.options || []}
                              onChange={(newOptions) => updateQuestion(q.id, 'options', newOptions)}
                              placeholder="Type option and press enter (e.g., Option 1)"
                            />
                            <p className="text-xs text-muted-foreground">
                              Press enter after each option to add it
                            </p>
                          </div>
                        )}
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={q.required}
                            onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
                          />
                          <span className="text-sm">Required</span>
                        </label>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Additional Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Instructions</CardTitle>
                <CardDescription>Any special notes for your guests</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Please arrive 15 minutes early for drinks reception..."
                  rows={5}
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                />
              </CardContent>
            </Card>

            {/* Submit Buttons */}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-4">
              <Button type="button" variant="outline" onClick={() => navigate(`/event/${eventId}`)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DashboardContentContainer>
      </main>
    </div>
  );
}

